import { Header } from "@/components/brand/Header";
import { Footer } from "@/components/brand/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { Clock, ShieldAlert, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

export default function PendingPage() {
  const { user, logout } = useAuth();
  const { t, formatDate } = useLocale();
  const navigate = useNavigate();
  const isRejected = user?.status === "rejected";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 mx-auto max-w-3xl px-4 sm:px-8 py-16 flex items-center">
        <div className="border border-border bg-card p-8 sm:p-12 w-full">
          <div className={`inline-flex items-center justify-center h-14 w-14 mb-6 ${isRejected ? "bg-red-500/10 text-red-500" : "bg-accent/10 text-accent"}`}>
            {isRejected ? <ShieldAlert className="h-6 w-6" strokeWidth={1.5} /> : <Clock className="h-6 w-6" strokeWidth={1.5} />}
          </div>
          <div className={`font-mono-data text-[10px] uppercase tracking-[0.3em] mb-3 ${isRejected ? "text-red-500" : "text-accent"}`}>
            {isRejected ? t("pending.rejected_label") : t("pending.label")}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-black mb-4 leading-[1.05]">
            {isRejected ? t("pending.rejected_title") : t("pending.title")}
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed">
            {isRejected ? t("pending.rejected_subtitle") : t("pending.subtitle")}
          </p>

          {isRejected && user?.rejection_reason && (
            <div className="mt-6 border border-red-500/30 bg-red-500/5 p-5" data-testid="pending-rejection-reason">
              <div className="font-mono-data text-[10px] uppercase tracking-wider text-red-500 mb-2">
                {t("pending.rejection_reason_label")}
                {user.rejected_at && (
                  <span className="ml-2 text-muted-foreground">· {formatDate(user.rejected_at)}</span>
                )}
              </div>
              <p className="text-sm text-foreground whitespace-pre-line">{user.rejection_reason}</p>
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-border border border-border text-xs">
            <Info label={t("pending.email")} value={user?.email} />
            <Info label={t("pending.company")} value={user?.razao_social} />
            <Info label={t("pending.country")} value={user?.country} />
          </div>

          <div className="mt-8 flex items-center flex-wrap gap-3">
            {isRejected && (
              <Button asChild className="h-10 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-xs uppercase tracking-wider">
                <a href="mailto:trade@kaffaexchange.com?subject=Revisão de cadastro Kaffa Exchange" data-testid="pending-contact-email">
                  <Mail className="h-3.5 w-3.5 mr-2" /> {t("pending.contact_cta")}
                </a>
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={async () => { await logout(); navigate("/"); }}
              className="h-10 rounded-none text-xs uppercase tracking-wider"
            >
              {t("pending.logout")}
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-card p-4">
      <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm mt-1 truncate">{value || "—"}</div>
    </div>
  );
}
